import {
  Exclude,
  Expose,
  instanceToPlain,
  plainToInstance,
  Transform,
  Type,
} from 'class-transformer';
import * as t from 'io-ts';
import { validateJSON, validateJSONNow } from '../web/validation';
import { Entity } from './entity';
import {
  NotificationTemplateAutoSubscribePolicy,
  NotificationTemplateAutoSubscribePolicyCodec,
  NotificationTemplateAutoSubscribePolicyEntity,
} from './notification-template-auto-subscribe-policy';
import {
  PersonCapabilityType,
  toPersonCapabilityType,
} from './person-capability-type';
import { getDatabaseCodeArrayTransformer } from './transfomers/transformers';
import {
  NotificationThreadTemplateThreadConfig,
  NotificationTemplateThreadConfigCodec,
  NotificationTemplateThreadConfigEntity,
} from './notification-thread-template-thread-config';

export const NotificationTemplateCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    emailSupported: t.boolean,
    pushSupported: t.boolean,
    smsSupported: t.boolean,
    hasAttachments: t.boolean,
    handlerKey: t.string,
    recurring: t.boolean,
    enabled: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    description: t.string,
    emailNotificationSenderProfileId: t.string,
    smsNotificationSenderProfileId: t.string,
    pushNotificationSenderProfileId: t.string,
    cronExpression: t.string,
    timeZone: t.string,
    filterSchema: t.string, // JSON
    createdById: t.string,
    lastModifiedById: t.string,

    threadConfig: NotificationTemplateThreadConfigCodec,
    autoSubPolicy: NotificationTemplateAutoSubscribePolicyCodec,
    scopedAutoSubRegionIds: t.array(t.string),
    scopedAutoSubCompanyIds: t.array(t.string),
    scopedAutoSubAudienceCapabilities: t.array(t.string),
    subscriberVisible: t.boolean,
    subscriberGroup: t.string,
    subscriberSortOrder: t.number,
  }),
]);

export type NotificationTemplate = t.TypeOf<typeof NotificationTemplateCodec>;

@Exclude()
export class NotificationTemplateEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Expose()
  emailSupported!: boolean;

  @Expose()
  pushSupported!: boolean;

  @Expose()
  smsSupported!: boolean;

  @Expose()
  hasAttachments!: boolean;

  @Expose()
  handlerKey!: string;

  @Expose()
  recurring!: boolean;

  @Expose()
  enabled!: boolean;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  // ─────────────────────────────
  // Optional fields
  // ─────────────────────────────

  @Expose()
  description?: string;

  @Expose()
  emailNotificationSenderProfileId?: string;

  @Expose()
  smsNotificationSenderProfileId?: string;

  @Expose()
  pushNotificationSenderProfileId?: string;

  @Expose()
  cronExpression?: string;

  @Expose()
  timeZone?: string;

  @Expose()
  filterSchema?: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  @Type(() => NotificationTemplateThreadConfigEntity)
  threadConfig?: NotificationTemplateThreadConfigEntity;

  @Expose()
  @Type(() => NotificationTemplateAutoSubscribePolicyEntity)
  autoSubPolicy?: NotificationTemplateAutoSubscribePolicyEntity;

  @Expose()
  scopedAutoSubRegionIds?: string[];

  @Expose()
  scopedAutoSubCompanyIds?: string[];

  @Expose()
  @Transform(getDatabaseCodeArrayTransformer(toPersonCapabilityType))
  scopedAutoSubAudienceCapabilities?: PersonCapabilityType[];

  @Expose()
  subscriberVisible?: boolean;

  @Expose()
  subscriberGroup?: string;

  @Expose()
  subscriberSortOrder?: number;

  readonly entityType = 'NOTIF_TEMPLATE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for NotificationTemplateEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: NotificationTemplate): NotificationTemplateEntity {
    return plainToInstance(NotificationTemplateEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<NotificationTemplate> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(NotificationTemplateCodec, object);
  }

  asTypeValue(): NotificationTemplate {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(NotificationTemplateCodec, object);
  }
}

export type NotificationTemplatePayload = Partial<
  Omit<NotificationTemplate, 'autoSubPolicy' | 'threadConfig'>
> & {
  threadConfig?: Partial<NotificationThreadTemplateThreadConfig>;
  autoSubPolicy?: Partial<NotificationTemplateAutoSubscribePolicy>;
};
